import React, { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button } from "@/components/ui/Button";
import { ScrollArea } from "@/components/ui/ScrollArea";
import { Braces, User, FileText, Database, Bot, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

// Types for dynamic variables
interface DynamicVariable {
    name: string;
    variable: string;
    description?: string;
}

interface VariableCategory {
    id: string;
    name: string;
    icon: React.ReactNode;
    variables: DynamicVariable[];
}

// Default available variables organized by category
const DEFAULT_VARIABLE_CATEGORIES: VariableCategory[] = [
    {
        id: "user",
        name: "User",
        icon: <User className="h-4 w-4" />,
        variables: [
            { name: "User Email", variable: "{{user.email}}", description: "Email address of the user" },
            { name: "User Phone", variable: "{{user.phone}}", description: "Phone number of the user" },
        ],
    },
    {
        id: "payload",
        name: "Payload",
        icon: <Database className="h-4 w-4" />,
        variables: [
            { name: "Message Body", variable: "{payload.body}", description: "Message content from payload" },
            { name: "Sender", variable: "{payload.from}", description: "Sender from payload" },
        ],
    },
    {
        id: "ai",
        name: "Action Classifier",
        icon: <Bot className="h-4 w-4" />,
        variables: [
            { name: "Action", variable: "{ai_result.output.type}", description: "AI node output" },
            { name: "Decision Threshold", variable: "{ai_result.output.confidence_score}", description: "Confidence score" },
        ],
    },
];

interface DynamicVariableInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    type?: "input" | "textarea";
    rows?: number;
    className?: string;
    categories?: VariableCategory[];
    formFields?: { id: string; label: string }[];
    disabled?: boolean;
}

export const DynamicVariableInput: React.FC<DynamicVariableInputProps> = ({
    value,
    onChange,
    placeholder,
    type = "input",
    rows = 4,
    className,
    categories = DEFAULT_VARIABLE_CATEGORIES,
    formFields = [],
    disabled = false,
}) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState<string | null>("user");

    // Build categories including form fields if available
    const allCategories = React.useMemo(() => {
        if (formFields.length === 0) return categories;

        const formFieldCategory: VariableCategory = {
            id: "form_fields",
            name: "Form Fields",
            icon: <FileText className="h-4 w-4 text-purple-500" />,
            variables: formFields.map((field) => ({
                name: field.label,
                variable: `{${field.id}}`,
                description: `Value of ${field.label}`,
            })),
        };

        return [formFieldCategory, ...categories];
    }, [categories, formFields]);

    // Insert variable at cursor position
    const insertVariable = (variable: string) => {
        const input = inputRef.current;
        if (!input) {
            // Fallback: append to end
            onChange(value + variable);
            setIsPopoverOpen(false);
            return;
        }

        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = value.substring(0, start) + variable + value.substring(end);

        onChange(newValue);
        setIsPopoverOpen(false);

        // Set cursor position after inserted variable
        setTimeout(() => {
            if (input) {
                const newPos = start + variable.length;
                input.focus();
                input.setSelectionRange(newPos, newPos);
            }
        }, 0);
    };

    const toggleCategory = (categoryId: string) => {
        setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    };

    const InputComponent = type === "textarea" ? "textarea" : "input";

    return (
        <div className="relative">
            <div className="flex gap-1">
                <div className="flex-1 relative">
                    <InputComponent
                        ref={inputRef as any}
                        type={type === "input" ? "text" : undefined}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        rows={type === "textarea" ? rows : undefined}
                        disabled={disabled}
                        className={cn(
                            "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500",
                            disabled && "bg-gray-100 cursor-not-allowed",
                            className
                        )}
                    />
                    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={disabled}
                                className={cn(
                                    "absolute right-1 h-7 w-7 p-0 hover:bg-blue-50",
                                    type === "textarea" ? "top-1" : "top-1/2 -translate-y-1/2"
                                )}
                                title="Insert dynamic variable"
                            >
                                <Braces className="h-4 w-4 text-blue-600" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent
                            className="w-72 p-0"
                            align="end"
                            side="bottom"
                            sideOffset={4}
                        >
                            <div className="p-3 border-b border-gray-100">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <Braces className="h-4 w-4 text-blue-600" />
                                    Insert Dynamic Variable
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Click to insert at cursor position
                                </p>
                            </div>
                            <ScrollArea className="h-72">
                                <div className="p-2">
                                    {allCategories.map((category) => (
                                        <div key={category.id} className="mb-1">
                                            <button
                                                type="button"
                                                onClick={() => toggleCategory(category.id)}
                                                className="w-full flex items-center justify-between px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                                            >
                                                <div className="flex items-center gap-2">
                                                    {category.icon}
                                                    {category.name}
                                                    <span className="text-xs text-gray-400">
                                                        ({category.variables.length})
                                                    </span>
                                                </div>
                                                <ChevronDown
                                                    className={cn(
                                                        "h-4 w-4 text-gray-400 transition-transform",
                                                        expandedCategory === category.id && "rotate-180"
                                                    )}
                                                />
                                            </button>
                                            {expandedCategory === category.id && (
                                                <div className="ml-2 mt-1 space-y-0.5">
                                                    {category.variables.map((variable) => (
                                                        <button
                                                            key={variable.variable}
                                                            type="button"
                                                            onClick={() => insertVariable(variable.variable)}
                                                            className="w-full text-left px-2 py-1.5 rounded-md hover:bg-blue-50 transition-colors group"
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm text-gray-700 group-hover:text-blue-700">
                                                                    {variable.name}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1 mt-0.5">
                                                                <code className="text-xs text-blue-600 bg-blue-50 px-1 rounded font-mono">
                                                                    {variable.variable}
                                                                </code>
                                                            </div>
                                                            {variable.description && (
                                                                <p className="text-xs text-gray-400 mt-0.5">
                                                                    {variable.description}
                                                                </p>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Variable pills hint - show when value contains variables */}
            {value && (value.includes("{{") || value.includes("{payload")) && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {extractVariables(value).map((v, idx) => (
                        <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded font-mono"
                        >
                            <Braces className="h-3 w-3" />
                            {v}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// Helper function to extract variables from text
function extractVariables(text: string): string[] {
    const variables: string[] = [];

    // Match {{variable}} pattern
    const doublePattern = /\{\{[^}]+\}\}/g;
    const doubleMatches = text.match(doublePattern);
    if (doubleMatches) {
        variables.push(...doubleMatches);
    }

    // Match {payload.xxx} pattern
    const singlePattern = /\{payload\.[^}]+\}/g;
    const singleMatches = text.match(singlePattern);
    if (singleMatches) {
        variables.push(...singleMatches);
    }

    // Remove duplicates
    return [...new Set(variables)];
}

// Export types and defaults for customization
export type { DynamicVariable, VariableCategory };
